<template>
  <li v-if="visible" class="extras-mg-nuevo">
    <Button
      type="primary"
      size="regular"
      icon="iconoir-plus"
      :loading="creando"
      :disabled="creando"
      @click="crear"
    >
      Nuevo elemento
    </Button>
  </li>
</template>

<script>
import Button from '@baserow/modules/core/components/Button.vue'
import { notifyIf } from '@baserow/modules/core/utils/error'

/**
 * El botón azul "Nuevo elemento" del principio de la barra, como el de Monday.
 *
 * Crea la fila por el mismo camino que "Insertar fila arriba" de la cuadrícula
 * (GridView.vue, addRow con `before`): justo antes de la primera fila que se ve
 * en pantalla y, si la vista está agrupada, en el grupo de esa fila y con sus
 * valores ya puestos. Así aparece donde el usuario está mirando: con la banda
 * de un grupo arriba, entra al principio de ese grupo, como en Monday. Al final
 * del grupo, en uno de cien filas, quedaba fuera de la pantalla y parecía que
 * el botón no había hecho nada. El core deja seleccionada la celda del nombre
 * para escribir en seguida.
 *
 * Sólo en la cuadrícula: el Kanban, el calendario y la línea de tiempo tienen
 * su propia forma de añadir, y en una vista pública no se crea nada.
 */
export default {
  name: 'BotonNuevoElemento',
  components: { Button },
  props: {
    database: { type: Object, required: false, default: null },
    table: { type: Object, required: true },
    view: { type: Object, required: false, default: null },
    fields: { type: Array, required: false, default: () => [] },
    isPublicView: { type: Boolean, required: false, default: false },
    storePrefix: { type: String, required: false, default: '' },
  },
  data() {
    return { creando: false }
  },
  computed: {
    visible() {
      return (
        !this.isPublicView &&
        this.view?.type === 'grid' &&
        !!this.database &&
        this.puedeCrear
      )
    },
    // La misma comprobación que GridView.vue (canCreateRow).
    puedeCrear() {
      if (this.table?.data_sync && !this.table.data_sync.two_way_sync) {
        return false
      }
      const workspaceId = this.database.workspace.id
      return (
        this.$hasPermission(
          'database.table.create_row',
          this.table,
          workspaceId
        ) ||
        this.$hasPermission(
          'database.table.view.create_row',
          this.view,
          workspaceId
        )
      )
    },
  },
  methods: {
    /**
     * Dónde entra la fila: antes de la primera que se ve entera en pantalla y,
     * agrupada, en su grupo. Sin filas a la vista (grupos plegados), al final
     * del primer grupo visible; sin agrupar y con la tabla vacía, al final.
     */
    dondeInsertar() {
      const getters = this.$store.getters
      const prefijo = this.storePrefix + 'view/grid/'
      const arriba = getters[prefijo + 'getScrollTop'] + this.altoTapado()
      const activos = getters[prefijo + 'getActiveGroupBys'] || []
      const campos = activos
        .map((g) => this.fields.find((f) => f.id === g.field))
        .filter(Boolean)
      if (campos.length === 0) {
        const indice =
          Math.ceil(arriba / getters[prefijo + 'getRowHeight']) -
          getters[prefijo + 'getBufferStartIndex']
        const fila = getters[prefijo + 'getAllRows'][indice]
        return { ruta: null, antes: fila || null }
      }
      // Sólo lo que está en pantalla (sin el margen que se pide de más).
      const items = getters[prefijo + 'getGroupByVisibleItems'](campos)
      const fila = items.find((i) => i.type === 'row' && i.y >= arriba)
      if (fila) {
        return { ruta: fila.path, antes: fila.row }
      }
      const cabecera = items.find((i) => i.type === 'header' && i.path)
      return { ruta: cabecera ? cabecera.path : null, antes: null }
    },
    /**
     * Lo que tapa el encabezado flotante de la piel (tabla agrupada, ver
     * piel-monday.css): una fila debajo de él no se ve. Es lo mismo que el
     * build le descuenta al core en scrollToElementRect. Sin encabezado
     * flotante, 0.
     */
    altoTapado() {
      const cuerpo = document.querySelector(
        '.grid-view__right .grid-view__inner > .grid-view__body'
      )
      const cabeza = cuerpo?.parentElement.querySelector(
        ':scope > .grid-view__head'
      )
      if (!cabeza) {
        return 0
      }
      return Math.max(
        0,
        cabeza.getBoundingClientRect().bottom -
          cuerpo.getBoundingClientRect().top
      )
    },
    async crear(event) {
      // El clic sube hasta el documento, y la celda que el core selecciona al
      // crear la fila lo tomaría como un clic fuera y se deseleccionaría en el
      // acto. Esta marca es la que el core respeta para no hacerlo.
      event.preventFieldCellUnselect = true
      this.creando = true
      const prefijo = this.storePrefix + 'view/grid/'
      const { ruta, antes } = this.dondeInsertar()
      const base = {
        view: this.view,
        table: this.table,
        fields: this.fields,
        values: {},
        before: antes,
        selectPrimaryCell: true,
      }
      try {
        if (ruta) {
          await this.$store.dispatch(prefijo + 'createNewRowInGroup', {
            ...base,
            path: ruta,
          })
        } else {
          await this.$store.dispatch(prefijo + 'createNewRow', base)
        }
      } catch (error) {
        notifyIf(error, 'row')
      } finally {
        this.creando = false
      }
    },
  },
}
</script>
